/**
 * The words a host puts around filling the script box, and around the box
 * itself.
 *
 * Here rather than beside either screen because two screens say them (mobile-app
 * ticket 14): the web and the phone both offer the sample (sample-script tickets
 * 01 and 02). The Doc strings were the web's until the phone grew the same
 * dialog, and what makes them shared rather than duplicated is that they are not
 * descriptions of a *screen*: they name an action, state the one thing about
 * Google Docs a first-time reader cannot guess, ask the one question worth
 * interrupting somebody for, decline a link that arrives mid-take, ask before
 * back on the room leaves a running take, give an empty box something to
 * read, and say on a first run why that is worth pressing. A phone that
 * reworded any of them would be a second answer to a question that has one.
 *
 * The failure sentences are not here — they are `google-docs-source.ts`'s,
 * because which of the five a response is and what to say about it are one
 * decision made in one place.
 */

/**
 * The button on the script card, and the title of the dialog it opens.
 *
 * They are deliberately the same words. The card used to ask *where does your
 * script come from* with a segmented control, and that is a question nobody
 * arrives wanting to answer: there is one script, and a Google Doc is a way of
 * filling the box that holds it. So this is a verb rather than a category, and
 * the dialog it opens is named after the press that opened it.
 */
export const LOAD_FROM_DOC = "Load from Google Doc";

/**
 * What to change in Google Docs, said before the load fails rather than only
 * after it.
 *
 * The sharing setting is the one thing about this a first-time visitor cannot
 * guess, and the refusal message the proxy sends back says the same words — so
 * somebody who reads neither still meets it twice.
 */
export const DOC_SHARING_HINT =
  "The document has to be link-shared: in Google Docs, Share → General access → " +
  "“Anyone with the link”. cuekards reads it and nothing else.";

/**
 * Before a fetched document lands on top of something already written.
 *
 * The browser's own `confirm` on the web and the platform's own alert on a
 * phone, which is the one place either screen uses the host's furniture. What it
 * is guarding is a box that *is* the script: there is no separate loaded copy to
 * fall back to, so the text about to be replaced is the only text there is. The
 * exact wording is the brief's.
 */
export const OVERWRITE_CONFIRM = "This will overwrite current script! OK?";

/**
 * Before `Clear` empties the box.
 *
 * The same guard as {@link OVERWRITE_CONFIRM}, for the same reason: the box is
 * the script, and a script that was only typed has no loaded copy, no document
 * and no remembered one to come back from. `Clear` sits on anything in the box,
 * a short reach from where you are typing, so one stray press would otherwise
 * be the whole script gone with no undo. Worded as the overwrite's twin.
 */
export const CLEAR_CONFIRM = "This will clear current script! OK?";

/**
 * When the system's back is pressed on the room while a take is running
 * (play-feedback ticket 01), and its two answers.
 *
 * Back on the room leaves the app, and leaving pauses a rolling take or cancels
 * a countdown without a word. So the press asks instead, and only then: a
 * general "are you sure you want to exit?" is what Android's guidelines advise
 * against, and with no take running leaving costs nothing. Neither answer
 * leaves. **Keep recording** is the press undone, and **Stop and save** ends
 * the take where the room can show it saved.
 *
 * Here beside the other two questions rather than beside the phone's screens,
 * so that a web host with a back of its own asks the same one. Only the phone
 * asks it today.
 */
export const STOP_ON_BACK_CONFIRM = "Stop and save the take?";
export const KEEP_RECORDING = "Keep recording";
export const STOP_AND_SAVE = "Stop and save";

/**
 * When a shared link arrives in the middle of a take (mobile-app ticket 15).
 *
 * A tapped link is a request to load a script, and loading one re-anchors the
 * prompter and rebuilds the follower over a new alignment index — which is the
 * one thing that must not happen while a recorder is rolling (FR-17,
 * `refreshAvailability`). The take wins, and the link is declined out loud: the
 * alternative is a link that silently does nothing, which reads as the app
 * having failed to handle it and invites a second tap.
 *
 * Here rather than beside the phone's screens because it is the same rule the
 * other two hosts keep — they simply cannot be handed a link mid-take, having
 * no operating system association to be handed one through. The day a tab can
 * be, this is what it says.
 */
export const LINK_DURING_TAKE =
  "A shared link arrived while you were recording. Finish the take, then tap it again.";

/**
 * The button on an empty script card that fills it with {@link SAMPLE_SCRIPT}
 * (sample-script ticket 01), and on the phone's empty box (ticket 02).
 *
 * It shows only while the box is empty, so it never overwrites anything and asks
 * nothing first. It does not load or start: the room's own button does that, as
 * for a paste.
 */
export const TRY_A_SAMPLE = "Try a sample";

/**
 * One line beside {@link TRY_A_SAMPLE} on a first run (play-feedback ticket 03):
 * the reason to press it. Testers asked for an onboarding tour, and the sample
 * already is one; this sentence is the way in. Shown on the phone until the
 * reader has pressed the sample or saved a take. Here so the web can say the
 * same thing later.
 */
export const FIRST_RUN_HINT =
  "Read the sample aloud to see how the prompter follows your voice.";

/**
 * A short script for somebody who has none yet, so they can find out whether the
 * prompter follows *their* voice before they go and find one.
 *
 * Written to teach the product as it is read: it waits at a breath, recovers
 * after a line skipped on purpose, and shows a note of each kind that is never
 * read. About a minute at the default 130 wpm. Its rules, each held by
 * `script-copy.test.ts`:
 *
 * - **No wake word.** Any wake form followed by a command word, across
 *   punctuation, a line break or inside brackets, turns voice commands off for
 *   the script (`findScriptCollision`), which is the wrong first impression.
 * - **Numbers in words.** `numbers.ts` reads `2026` as "two thousand twenty six"
 *   and `3.5` as "three five".
 * - **The first line is a sentence.** `scriptTitle` takes the first non-empty
 *   line, hash and all, and names the take's file after it (FR-48).
 * - **`[brackets]` and `#` lines only.** No notation that has not landed.
 *
 * Once started it is remembered like any paste, until Clear.
 *
 * Clear is on both screens: the web's under the box, and the phone's in the
 * sample's own place beside `Load from Google Doc`, which it can share because
 * one is drawn on an empty box and the other on a full one.
 */
export const SAMPLE_SCRIPT = [
  "This is a sample script, and you are reading it out loud.",
  "Watch the words. They move when you speak, and wait when you stop.",
  "[Stop for a breath here. Count to three in your head.]",
  "See? Nothing moved without you.",
  "Now skip the next line on purpose.",
  "This is the line to skip, so jump straight past it.",
  "It finds you again after a few words.",
  "Lines in square brackets, like the one above, are shown and never read.",
  "# So is a line that starts with a hash, like this one.",
  "Read at your own pace. Slow down, speed up, it keeps up with you.",
  "Go back a line and read it again, and the words go back with you.",
  "When you are done, go back, press Clear, and paste your own script.",
].join("\n");
